import { useEffect, useRef } from "preact/hooks";
import { t } from "../i18n";
import { connectedPeerIds } from "../data/filters";
import type { RadarEntity, StaticEvidenceProvider } from "../data/provider";
import { hrefFor } from "../app/router";
import { IconClose } from "../components/icons";

function typeLabel(type: RadarEntity["type"]): string {
  if (type === "technology") return t("radar.technologies");
  if (type === "skill") return t("radar.skills");
  if (type === "career") return t("radar.careers");
  return t("radar.sectors");
}

export function RadarHelp() {
  return (
    <div class="obs-insight-help">
      <p class="obs-eyebrow">{t("radar.help.select")}</p>
      <h2>{t("radar.help.title")}</h2>
      <p>{t("radar.help.body")}</p>
      <p>{t("radar.help.filter")}</p>
    </div>
  );
}

export function EntityPanel({
  entity,
  provider,
  skillPool,
  onClose,
  onOpenRelated,
  variant,
}: {
  entity: RadarEntity;
  provider: StaticEvidenceProvider;
  skillPool?: RadarEntity[];
  onClose: () => void;
  onOpenRelated?: (id: string, type: RadarEntity["type"]) => void;
  variant: "side" | "sheet";
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const peerPool =
    skillPool ??
    provider.radarEntities("skill").filter((e) => !entity.groupId || e.groupId === entity.groupId);
  const skillIds = entity.type === "skill" ? connectedPeerIds(entity, peerPool) : entity.relatedSkillIds;
  const skills = skillIds.map((id) => provider.skillById(id)).filter(Boolean);
  const careers = entity.relatedCareerIds.map((id) => provider.careerById(id)).filter(Boolean);
  const sources = entity.sourceIds.map((id) => provider.sourceById(id)).filter(Boolean);
  const resources = entity.relatedResourceIds.map((id) => provider.resourceById(id)).filter(Boolean);
  const sectors = entity.relatedSectorIds.map((id) => provider.sectors().find((s) => s.id === id)).filter(Boolean);
  const techs = entity.relatedTechnologyIds
    .map((id) => provider.technologies().find((s) => s.id === id))
    .filter(Boolean);
  const relatedCareer = careers[0];

  useEffect(() => {
    if (variant !== "sheet") return;
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [entity.id, variant, onClose]);

  return (
    <>
      {variant === "sheet" ? <div class="obs-backdrop" onClick={onClose} /> : null}
      <aside
        class={variant === "sheet" ? "obs-sheet" : "obs-insight"}
        role={variant === "sheet" ? "dialog" : "region"}
        aria-modal={variant === "sheet" ? true : undefined}
        aria-labelledby="obs-insight-title"
      >
        <header class="obs-insight-head">
          <p class="obs-eyebrow">{typeLabel(entity.type)}</p>
          <button ref={closeRef} class="obs-icon-btn" type="button" onClick={onClose} aria-label={t("radar.close")}>
            <IconClose size={18} />
          </button>
        </header>
        <h2 id="obs-insight-title">{entity.name}</h2>
        <p class="obs-drawer-meta">
          <span class={`obs-badge obs-badge-${entity.trendStatus}`}>{t(`radar.${entity.trendStatus}`)}</span>
          {entity.groupId ? <span class="obs-badge">{t(`group.${entity.groupId}`)}</span> : null}
          {entity.geography.map((geo) => (
            <span class="obs-badge" key={geo}>
              {t(`radar.${geo}`)}
            </span>
          ))}
        </p>
        <p>{entity.description}</p>
        <section class="obs-drawer-block">
          <h3>{t("radar.why")}</h3>
          <p>{entity.whyItMatters}</p>
        </section>
        {skills.length > 0 && (
          <section class="obs-drawer-block">
            <h3>{t("radar.relatedSkills")}</h3>
            <ul class="obs-chip-list">
              {skills.map((s) => (
                <li key={s!.id}>
                  {onOpenRelated ? (
                    <button class="obs-chip-btn" type="button" onClick={() => onOpenRelated(s!.id, "skill")}>
                      {s!.name}
                    </button>
                  ) : (
                    s!.name
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
        {careers.length > 0 && (
          <section class="obs-drawer-block">
            <h3>{t("radar.relatedCareers")}</h3>
            <ul class="obs-chip-list">
              {careers.map((c) => (
                <li key={c!.id}>
                  {onOpenRelated ? (
                    <button class="obs-chip-btn" type="button" onClick={() => onOpenRelated(c!.id, "career")}>
                      {c!.title}
                    </button>
                  ) : (
                    c!.title
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
        {techs.length > 0 && (
          <section class="obs-drawer-block">
            <h3>{t("radar.relatedTech")}</h3>
            <ul class="obs-chip-list">
              {techs.map((c) => (
                <li key={c!.id}>
                  {onOpenRelated ? (
                    <button class="obs-chip-btn" type="button" onClick={() => onOpenRelated(c!.id, "technology")}>
                      {c!.name}
                    </button>
                  ) : (
                    c!.name
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
        {sectors.length > 0 && (
          <section class="obs-drawer-block">
            <h3>{t("radar.relatedSectors")}</h3>
            <ul class="obs-chip-list">
              {sectors.map((c) => (
                <li key={c!.id}>
                  {onOpenRelated ? (
                    <button class="obs-chip-btn" type="button" onClick={() => onOpenRelated(c!.id, "sector")}>
                      {c!.name}
                    </button>
                  ) : (
                    c!.name
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
        <section class="obs-drawer-block">
          <h3>{t("radar.connection")}</h3>
          {resources.length ? (
            <ul class="obs-list">
              {resources.map((r) => (
                <li key={r!.id}>
                  {r!.url ? (
                    <a href={r!.url} rel="noopener noreferrer">
                      {r!.title}
                    </a>
                  ) : (
                    r!.title
                  )}{" "}
                  {r!.availability === "coming-soon" ? `(${t("match.comingSoon")})` : ""}
                </li>
              ))}
            </ul>
          ) : (
            <p>{t("match.noResources")}</p>
          )}
        </section>
        <section class="obs-drawer-block">
          <h3>{t("radar.evidence")}</h3>
          <ul class="obs-list">
            {sources.map((s) => (
              <li key={s!.id}>
                <strong>{s!.name}</strong> — {s!.title}
                {s!.url ? (
                  <>
                    {" "}
                    <a href={s!.url} rel="noopener noreferrer">
                      {s!.organization}
                    </a>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
        <dl class="obs-drawer-facts">
          <div>
            <dt>{t("radar.reviewed")}</dt>
            <dd>{entity.lastReviewedAt}</dd>
          </div>
          <div>
            <dt>{t("radar.coverage")}</dt>
            <dd>{entity.geography.map((geo) => t(`radar.${geo}`)).join(", ")}</dd>
          </div>
        </dl>
        {relatedCareer && (
          <p class="obs-drawer-cta">
            <a class="obs-btn obs-btn-primary" href={hrefFor("/skills-match", `?career=${relatedCareer.id}`)}>
              {t("radar.assess")}
            </a>
          </p>
        )}
      </aside>
    </>
  );
}
